import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'

import { TwoLevelPickerGroupContainer } from 'containers/aiSegmentBuilder/picker'
import {
  removeGroup,
  selectGroupIds,
  selectNextAvailablePickerId,
} from 'slices/aiSegmentBuilder/picker'

import {
  buildSubmissionPayload,
  cleanupPickersBeforeSubmit,
  loadPickerGroup,
  PickerSubmissionPayload,
  SAMPLE_SERVER_RESPONSE,
  ServerPickerResponse,
} from './hooks'

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const pageStyle: React.CSSProperties = {
  display: 'flex',
  gap: '24px',
  padding: '24px',
  maxWidth: '1600px',
  margin: '0 auto',
}

const leftPanelStyle: React.CSSProperties = {
  flex: '1 1 50%',
  minWidth: '400px',
}

const rightPanelStyle: React.CSSProperties = {
  flex: '1 1 50%',
  minWidth: '400px',
}

const payloadPanelStyle: React.CSSProperties = {
  position: 'sticky' as const,
  top: '24px',
  padding: '16px',
  border: '2px solid #10b981',
  borderRadius: '8px',
  backgroundColor: '#f0fdf4',
}

const payloadTitleStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#166534',
  marginBottom: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const payloadTextareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '400px',
  padding: '12px',
  border: '1px solid #86efac',
  borderRadius: '6px',
  fontFamily: 'monospace',
  fontSize: '12px',
  resize: 'vertical',
  backgroundColor: 'white',
}

const payloadEmptyStyle: React.CSSProperties = {
  color: '#6b7280',
  textAlign: 'center',
  padding: '40px 20px',
  fontStyle: 'italic',
}

const testPanelStyle: React.CSSProperties = {
  marginBottom: '32px',
  padding: '16px',
  border: '2px dashed #d1d5db',
  borderRadius: '8px',
  backgroundColor: '#f9fafb',
}

const testPanelTitleStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '150px',
  padding: '12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontFamily: 'monospace',
  fontSize: '12px',
  resize: 'vertical',
  marginBottom: '12px',
}

const testButtonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
}

const addGroupButtonStyle: React.CSSProperties = {
  background: '#10b981',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  cursor: 'pointer',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
}

const loadSampleButtonStyle: React.CSSProperties = {
  background: '#6366f1',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  cursor: 'pointer',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
}

const errorStyle: React.CSSProperties = {
  color: '#dc2626',
  fontSize: '12px',
  marginTop: '8px',
}

const groupsContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}

const groupWrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}

const removeButtonStyle: React.CSSProperties = {
  alignSelf: 'flex-end',
  background: '#ef4444',
  color: 'white',
  border: 'none',
  padding: '6px 12px',
  cursor: 'pointer',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 500,
}

const clearButtonStyle: React.CSSProperties = {
  marginTop: '12px',
  background: '#6b7280',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  cursor: 'pointer',
  borderRadius: '4px',
  fontSize: '13px',
  fontWeight: 500,
}

const headerStyle: React.CSSProperties = {
  marginBottom: '20px',
}

const titleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 600,
  color: '#111827',
  margin: 0,
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Generate a unique group ID */
const generateGroupId = (): string => {
  return `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════

const ReviewItems = () => {
  const dispatch = useDispatch()
  const store = useStore()

  // Get all group IDs from Redux
  const groupIds = useSelector(selectGroupIds)

  // Get next available picker ID for globally unique IDs when creating new groups
  const nextPickerId = useSelector(selectNextAvailablePickerId)

  // Local state for the test textarea
  const [jsonInput, setJsonInput] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)

  // State for extracted payloads (keyed by groupId)
  const [extractedPayloads, setExtractedPayloads] = useState<
    Record<string, PickerSubmissionPayload>
  >({})

  // Load sample JSON into textarea
  const handleLoadSample = useCallback(() => {
    setJsonInput(JSON.stringify(SAMPLE_SERVER_RESPONSE, null, 2))
    setParseError(null)
  }, [])

  // Parse JSON and create a new group
  const handleAddGroup = useCallback(() => {
    try {
      setParseError(null)

      if (!jsonInput.trim()) {
        setParseError('Please enter a JSON response')
        return
      }

      const serverResponse: ServerPickerResponse = JSON.parse(jsonInput)

      // Validate required fields
      if (!serverResponse.level_1) {
        setParseError('Invalid server response: missing level_1')
        return
      }
      if (
        !serverResponse.selections ||
        !Array.isArray(serverResponse.selections)
      ) {
        setParseError(
          'Invalid server response: missing or invalid selections array'
        )
        return
      }

      // Generate a unique group ID
      const newGroupId = generateGroupId()

      // Use the loadPickerGroup function to hydrate the group
      // Pass nextPickerId to ensure globally unique picker IDs across groups
      loadPickerGroup(dispatch, newGroupId, serverResponse, nextPickerId)

      // Clear the input after successful creation
      setJsonInput('')
    } catch (e) {
      setParseError(
        `JSON parse error: ${e instanceof Error ? e.message : 'Unknown error'}`
      )
    }
  }, [jsonInput, dispatch, nextPickerId])

  // Handle textarea change
  const handleJsonInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setJsonInput(e.target.value)
      setParseError(null)
    },
    []
  )

  // Handle confirm from a group - extract and display the payload
  const handleGroupConfirm = useCallback(
    (groupId: string) => {
      // Get current state for cleanup decisions
      const currentState = store.getState()

      // Clean up pickers before building payload:
      // - Remove pickers with no top selection
      // - Update operator for pickers with no bottom selections
      cleanupPickersBeforeSubmit(dispatch, currentState, groupId)

      // Get fresh state after cleanup and build payload
      const freshState = store.getState()
      const payload = buildSubmissionPayload(freshState, groupId)
      setExtractedPayloads(prev => ({
        ...prev,
        [groupId]: payload,
      }))
    },
    [dispatch, store]
  )

  // Handle removing a group
  const handleRemoveGroup = useCallback(
    (groupId: string) => {
      dispatch(removeGroup({ groupId }))
      // Also remove from extracted payloads if present
      setExtractedPayloads(prev => {
        const { [groupId]: _, ...rest } = prev
        return rest
      })
    },
    [dispatch]
  )

  // Handle clearing all extracted payloads
  const handleClearPayloads = useCallback(() => {
    setExtractedPayloads({})
  }, [])

  // Combine all extracted payloads for display
  const allPayloadsText = Object.entries(extractedPayloads)
    .map(
      ([groupId, payload]) =>
        `// Group: ${groupId}\n${JSON.stringify(payload, null, 2)}`
    )
    .join('\n\n')

  return (
    <div style={pageStyle}>
      {/* Left Panel - Picker Groups */}
      <div style={leftPanelStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Review Items - Group Test</h1>
        </div>

        {/* Test Panel - Add Group */}
        <div style={testPanelStyle}>
          <div style={testPanelTitleStyle}>
            Test Panel - Add Group from JSON
          </div>
          <textarea
            style={textareaStyle}
            value={jsonInput}
            onChange={handleJsonInputChange}
            placeholder="Paste a ServerPickerResponse JSON here..."
          />
          <div style={testButtonRowStyle}>
            <button
              type="button"
              onClick={handleLoadSample}
              style={loadSampleButtonStyle}
            >
              Load Sample JSON
            </button>
            <button
              type="button"
              onClick={handleAddGroup}
              style={addGroupButtonStyle}
            >
              Add Group
            </button>
          </div>
          {parseError && <div style={errorStyle}>{parseError}</div>}
        </div>

        {/* Groups List */}
        <div style={groupsContainerStyle}>
          {groupIds.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center' }}>
              No groups yet. Paste a JSON response above and click "Add Group"
              to create one.
            </p>
          ) : (
            groupIds.map(groupId => (
              <div key={groupId} style={groupWrapperStyle}>
                <button
                  type="button"
                  onClick={() => handleRemoveGroup(groupId)}
                  style={removeButtonStyle}
                  aria-label={`Remove group ${groupId}`}
                >
                  Remove
                </button>
                <TwoLevelPickerGroupContainer
                  groupId={groupId}
                  hasHydrated={true}
                  onConfirm={handleGroupConfirm}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Extracted Payloads */}
      <div style={rightPanelStyle}>
        <div style={payloadPanelStyle}>
          <div style={payloadTitleStyle}>Extracted Submission Payloads</div>
          {Object.keys(extractedPayloads).length === 0 ? (
            <div style={payloadEmptyStyle}>
              Click "Confirm" on a picker group to see the extracted submission
              payload here.
            </div>
          ) : (
            <>
              <textarea
                style={payloadTextareaStyle}
                value={allPayloadsText}
                readOnly
              />
              <button
                type="button"
                onClick={handleClearPayloads}
                style={clearButtonStyle}
              >
                Clear
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReviewItems
